import parse, { type HTMLReactParserOptions } from 'html-react-parser';
import DOMPurify, { type Config } from 'dompurify';
import {
  createContext,
  useContext,
  useEffect,
  type FC,
  type ReactNode,
} from 'react';
import { clientLogger } from '../../utils/logger';
import { seedTranslations } from './seed';

// The server dictionary hydrates over this seed; until then (and if it never
// arrives) the seeded error-boundary and footer strings still resolve. The
// global store is initialized synchronously so module-load rawString calls
// (e.g. the error boundary title in main.tsx) aren't empty.
let globalTranslations: Record<string, string> = seedTranslations;

export const setGlobalTranslations = (translations: Record<string, string>) => {
  globalTranslations = translations;
};

export const getGlobalTranslations = (): Record<string, string> => {
  return globalTranslations;
};

// Create a context for translations - hydrated externally over the seed
const LanguageContext = createContext<Record<string, string>>(seedTranslations);

const DEFAULT_PURIFY_CONFIG: Config = {
  ADD_ATTR: ['target'],
};

type TokenCore = {
  replacementTokens?: { [key: string]: string };
  content?: string;
};

export type LanguageCore = {
  langKey?: string | string[];
  fallback?: string;
  purifyOptions?: Config;
} & TokenCore;

const TOKEN_PATTERN = /{{\s*([^{}\s]+)\s*}}/g;

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]!);

const fillTokens = (content: string, tokens: Record<string, string>) =>
  content.replace(TOKEN_PATTERN, (_, key: string) => {
    const value = Object.hasOwn(tokens, key) ? tokens[key] : undefined;
    return typeof value === 'string' ? escapeHtml(value) : '';
  });

/**
 * The tokens in `content` that `tokens` gives no value for.
 *
 * Replacement blanks an unfilled token, which is right for a heading — "Your order  has
 * shipped" beats a literal `{{order.number}}` — and wrong for a line that is *about* the
 * value, where "ship by ." asserts nothing. A caller that would rather omit such a line asks
 * here first, against the copy as authored rather than the replaced string, since after
 * replacement the hole is indistinguishable from a space.
 */
export const unfilledTokens = (
  content: string,
  tokens: Record<string, string> = {},
): string[] =>
  [...content.matchAll(TOKEN_PATTERN)]
    .map(([, key]) => key!)
    .filter((key) => !tokens[key]);

// Hook to access translations from context
export const useLanguageData = (): Record<string, string> => {
  return useContext(LanguageContext);
};

// Legacy function for non-component usage (e.g., validators)
// Now uses global translations which are kept in sync with the context
export const languageData = (): Record<string, string> => {
  return globalTranslations;
};

export const rawString = ({
  langKey,
  content: contentConfig,
  replacementTokens,
  fallback,
  purifyOptions,
  translations, // Optional: pass translations directly
}: LanguageCore & { translations?: Record<string, string> }) => {
  let baseText;

  // Use provided translations or fall back to global translations
  const language = translations || languageData();

  // Normalize keys to lowercase for consistent lookup
  baseText = Array.isArray(langKey)
    ? langKey
        .map((key) => language[key.toLowerCase()])
        .find((value) => value != null && value !== '')
    : langKey
      ? language[langKey.toLowerCase()]
      : undefined;

  if (contentConfig) {
    baseText = contentConfig;
  }
  if (!baseText && fallback) {
    baseText = fallback;
  }

  if (!baseText) return undefined;

  const sanitize = (html: string) =>
    DOMPurify.sanitize(html, { ...DEFAULT_PURIFY_CONFIG, ...purifyOptions });

  // A token value is text, and some come from the URL. Sanitizing the copy first leaves every
  // token in a text node or a double-quoted attribute value, where the escaped value can neither
  // open an element nor end the attribute; unquoted, `href={{x}}` would end at a space in the
  // value and let the rest add attributes. The second pass holds the value to the rules of the
  // attribute it fills, such as no `javascript:` in an `href`.
  const filled = fillTokens(
    sanitize(baseText.toString()),
    replacementTokens ?? {},
  );

  if (!filled) return undefined;

  return sanitize(filled);
};

/**
 * Whether a copy line is worth drawing: it resolves to non-blank text and every token it names
 * has a value. A default built around a token the request cannot fill — a ship-by date, a
 * submission date — is omitted rather than drawn with a hole. The check is against the copy *as
 * authored*, looked up here without replacement, not `rawString`'s output: replacement blanks an
 * unfilled token to a space, after which `unfilledTokens` can no longer see it. Resolved against
 * the context dictionary the caller passes, since the module-global one fills in an effect and
 * reads empty on first paint.
 */
export const copyFillsTokens = ({
  langKey,
  tokens = {},
  translations,
}: {
  langKey: LanguageCore['langKey'];
  tokens?: Record<string, string>;
  translations?: Record<string, string>;
}): boolean => {
  const language = translations ?? languageData();
  const authored = Array.isArray(langKey)
    ? langKey
        .map((key) => language[key.toLowerCase()])
        .find((value) => value != null && value !== '')
    : langKey
      ? language[langKey.toLowerCase()]
      : undefined;
  return !!authored?.trim() && unfilledTokens(authored, tokens).length === 0;
};

type LanguageProps = LanguageCore & {
  parseOptions?: HTMLReactParserOptions;
  purifyOptions?: Config;
};

export const Text: FC<LanguageProps> = ({
  langKey,
  content: contentConfig,
  fallback,
  replacementTokens = {},
  parseOptions = {},
  purifyOptions = {},
}: LanguageProps) => {
  // Get translations from context
  const translations = useLanguageData();

  if (!langKey && !contentConfig && !fallback) return null;

  const outputString = rawString({
    langKey,
    content: contentConfig,
    replacementTokens,
    fallback,
    purifyOptions,
    translations, // Pass context translations to rawString
  });

  if (!outputString) {
    return null;
  }

  try {
    return parse(outputString, parseOptions);
  } catch (error) {
    clientLogger.logError(error);
    return null;
  }
};

// Provider component to hydrate translations externally
interface LanguageProviderProps {
  translations: Record<string, string>;
  children: ReactNode;
}

export const LanguageProvider: FC<LanguageProviderProps> = ({
  translations,
  children,
}) => {
  // Keep global translations in sync with context for non-component usage
  useEffect(() => {
    setGlobalTranslations(translations);
  }, [translations]);

  return (
    <LanguageContext.Provider value={translations}>
      {children}
    </LanguageContext.Provider>
  );
};
